// ─────────────────────────────────────────────────────────────────────────────
// DiagnosticsCard — Socket.IO connection diagnostics for the desktop homepage
// right panel (Chunk 5). Reads the app-wide singleton (getRexySocket) and tracks:
//   • WebSocket connection status (connect / disconnect)
//   • last engine ping / pong timestamps (engine.io heartbeat)
//   • reconnection count (Manager 'reconnect')
//   • active transport type (websocket / polling, follows upgrades)
//
// Engine-level events ('ping' / 'pong' / 'upgrade') aren't in the public socket.io
// client typings, so the engine is accessed via a narrow `any` — the rest of the
// socket surface stays typed. If an engine event never fires (older engine), the
// timestamp simply stays N/A rather than being faked.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { REXY_CONSOLE } from '../lib/rexyConsole';
import { getRexySocket } from '../hooks/useRexySocket';

const MONO = "'Courier New', monospace";

function fmtTime(ts: number | null): string {
  if (ts == null) return 'N/A';
  return new Date(ts).toLocaleTimeString('en-GB', { hour12: false });
}

function StatRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={styles.row}>
      <span style={styles.label}>{label}</span>
      <span style={{ ...styles.value, color: color ?? REXY_CONSOLE.primary }}>{value}</span>
    </div>
  );
}

export default function DiagnosticsCard() {
  const [connected, setConnected] = useState(false);
  const [transport, setTransport] = useState<string | null>(null);
  const [reconnects, setReconnects] = useState(0);
  const [lastPing, setLastPing] = useState<number | null>(null);
  const [lastPong, setLastPong] = useState<number | null>(null);

  useEffect(() => {
    const socket = getRexySocket();
    const mgr = socket.io;
    let engine: any = null;

    const syncTransport = () => {
      const e: any = mgr.engine;
      setTransport(e?.transport?.name ?? null);
    };
    const onPing = () => setLastPing(Date.now());
    const onPong = () => setLastPong(Date.now());

    const bindEngine = () => {
      engine = mgr.engine as any;
      if (!engine) return;
      syncTransport();
      engine.on('ping', onPing);
      engine.on('pong', onPong);
      engine.on('upgrade', syncTransport); // polling → websocket upgrade
    };
    const unbindEngine = () => {
      if (!engine) return;
      engine.off('ping', onPing);
      engine.off('pong', onPong);
      engine.off('upgrade', syncTransport);
      engine = null;
    };

    const onConnect = () => {
      setConnected(true);
      unbindEngine();
      bindEngine(); // fresh engine instance after each (re)connect
    };
    const onDisconnect = () => setConnected(false);
    const onReconnect = () => setReconnects((c) => c + 1);

    setConnected(socket.connected);
    if (socket.connected) bindEngine();

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    mgr.on('reconnect', onReconnect);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      mgr.off('reconnect', onReconnect);
      unbindEngine();
    };
  }, []);

  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>║ DIAGNOSTICS</div>
      <StatRow
        label="LINK"
        value={connected ? 'ONLINE' : 'OFFLINE'}
        color={connected ? REXY_CONSOLE.primary : REXY_CONSOLE.critical}
      />
      <StatRow label="TRANSPORT" value={transport ?? 'N/A'} />
      <StatRow label="RECONNECTS" value={String(reconnects)} />
      <StatRow label="LAST PING" value={fmtTime(lastPing)} color={REXY_CONSOLE.dimText} />
      <StatRow label="LAST PONG" value={fmtTime(lastPong)} color={REXY_CONSOLE.dimText} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
  },
  sectionHeader: {
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 10,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '3px 0',
  },
  label: {
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.primary,
  },
  value: {
    fontFamily: MONO,
    fontSize: 11,
    textAlign: 'right',
  },
};
